import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import type { CreateAndAttachLabels, CreateAndAttachLabelsVariables } from 'types/graphql';
import LabelsApi from './Labels.api';
import { useLabelAttachmentsStore } from './labelAttachments.store';
import { useLabelDefinitionsStore } from './labelDefinitions.store';

export default function useBulkLabelAttachment() {
  const getNewLabelAttachments = useLabelAttachmentsStore((state) => state.getNewAttachments);
  const replaceLabels = useLabelAttachmentsStore((state) => state.replaceLabels);
  const _removeLabels = useLabelAttachmentsStore((state) => state.removeLabels);
  const attachNewLabels = useLabelAttachmentsStore((state) => state.attachNewLabels);

  const clearNewDefinitions = useLabelDefinitionsStore((state) => state.clearNewDefinitions);
  const getNewLabelDefinitions = useLabelDefinitionsStore((state) => state.getNewDefinitions);
  const addDefinitions = useLabelDefinitionsStore((state) => state.addDefinitions);

  const [createAndAttachLabels] = useMutation<CreateAndAttachLabels, CreateAndAttachLabelsVariables>(
    LabelsApi.CreateAndAttachLabels
  );

  return useCallback(async () => {
    const { newLabelAttachments, attachTo } = getNewLabelAttachments();
    const { newLabelDefinitions, labelTypes } = getNewLabelDefinitions();

    // if attach to is not provided, it's expected that the creation will be handled elsewhere.
    if (!attachTo) {
      attachNewLabels(false);

      return;
    }

    const labelsToCreate = newLabelDefinitions.map((label) => ({
      label: label.label,
      color: label.color,
      description: '',
      labelTypes,
    }));

    // new label definitions are also in attachments
    const labelsToAttach = newLabelAttachments.flatMap((def) => (def.id ? [def.id] : []));

    const input = { labelsToCreate, labelsToAttach, attachTo };

    attachNewLabels();
    clearNewDefinitions();

    await createAndAttachLabels({
      variables: { input },
      onCompleted: (res) => {
        const { labelAttachments, labelDefinitions } = res.createAndAttachLabels;

        replaceLabels(labelAttachments);
        addDefinitions(labelDefinitions);
      },
    });
  }, [
    createAndAttachLabels,
    getNewLabelAttachments,
    replaceLabels,
    addDefinitions,
    attachNewLabels,
    clearNewDefinitions,
    getNewLabelDefinitions,
  ]);
}
